import React, { useState } from "react";
import { api, getErr } from "@/lib/api";
import { toast } from "sonner";
import { X } from "@phosphor-icons/react";
import { CRM_SOURCES, SERVICE_TYPES } from "@/lib/crm";

// FR-2: quick lead entry — required: name, phone OR email, service, source.
export default function NewLeadDialog({ role, vas = [], onClose, onCreated }) {
  const [f, setF] = useState({
    prospect_name: "", prospect_phone: "", prospect_email: "",
    service_type: "routine", source: role === "admin" ? "inbound_call" : "va_outreach",
    prospect_address: "", est_value: "", notes: "", assign_to: "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = {
        prospect_name: f.prospect_name.trim(),
        prospect_phone: f.prospect_phone.trim() || null,
        prospect_email: f.prospect_email.trim() || null,
        service_type: f.service_type,
        source: f.source,
        prospect_address: f.prospect_address.trim() || null,
        est_value: f.est_value === "" ? null : Number(f.est_value),
        notes: f.notes.trim() || null,
      };
      if (role === "admin" && f.assign_to) body.assign_to = f.assign_to;
      await api.post("/crm/leads", body);
      toast.success("Lead created");
      onCreated?.();
    } catch (er) {
      const d = er?.response?.data?.detail;
      toast.error(typeof d === "object" && d?.message ? d.message : getErr(er));
    } finally { setBusy(false); }
  };

  const inputCls = "mt-1 h-9 w-full border border-[#030712] px-2 text-sm";
  return (
    <div className="fixed inset-0 z-[95] flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()}
        className="max-h-[92vh] w-full max-w-md overflow-y-auto bg-white p-5" data-testid="crm-new-lead-dialog">
        <div className="mb-3 flex items-center justify-between">
          <div className="font-display text-lg font-black">New lead</div>
          <button type="button" onClick={onClose} className="p-1 hover:bg-[#F3F4F6]"><X size={16} /></button>
        </div>
        <div className="space-y-3 text-xs">
          <label className="block"><span className="text-[#6B7280]">Name *</span>
            <input required data-testid="new-lead-name" value={f.prospect_name} onChange={set("prospect_name")} className={inputCls} /></label>
          <div className="grid grid-cols-2 gap-2">
            <label className="block"><span className="text-[#6B7280]">Phone</span>
              <input data-testid="new-lead-phone" value={f.prospect_phone} onChange={set("prospect_phone")} className={inputCls} /></label>
            <label className="block"><span className="text-[#6B7280]">Email</span>
              <input type="email" data-testid="new-lead-email" value={f.prospect_email} onChange={set("prospect_email")} className={inputCls} /></label>
          </div>
          <div className="text-[10px] text-[#6B7280]">Phone or email required — duplicates are auto-detected.</div>
          <div className="grid grid-cols-2 gap-2">
            <label className="block"><span className="text-[#6B7280]">Service *</span>
              <select data-testid="new-lead-service" value={f.service_type} onChange={set("service_type")} className={`${inputCls} bg-white`}>
                {SERVICE_TYPES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select></label>
            <label className="block"><span className="text-[#6B7280]">Source *</span>
              <select data-testid="new-lead-source" value={f.source} onChange={set("source")} className={`${inputCls} bg-white`}>
                {CRM_SOURCES.filter((s) => s.key !== "web_form").map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select></label>
          </div>
          <label className="block"><span className="text-[#6B7280]">Property address</span>
            <input data-testid="new-lead-address" value={f.prospect_address} onChange={set("prospect_address")} className={inputCls} /></label>
          <label className="block"><span className="text-[#6B7280]">Estimated value ($)</span>
            <input type="number" min="0" data-testid="new-lead-value" value={f.est_value} onChange={set("est_value")} className={inputCls} /></label>
          {role === "admin" && (
            <label className="block"><span className="text-[#6B7280]">Assign to VP (locks attribution)</span>
              <select data-testid="new-lead-assign" value={f.assign_to} onChange={set("assign_to")} className={`${inputCls} bg-white`}>
                <option value="">Leave unassigned</option>
                {vas.map((v) => <option key={v.user_id} value={v.user_id}>{v.name}</option>)}
              </select></label>
          )}
          <label className="block"><span className="text-[#6B7280]">Notes</span>
            <textarea rows={2} data-testid="new-lead-notes" value={f.notes} onChange={set("notes")} className="mt-1 w-full border border-[#030712] p-2 text-sm" /></label>
          <button type="submit" disabled={busy} data-testid="new-lead-submit"
            className="h-10 w-full bg-[#0044FF] text-xs font-bold uppercase tracking-widest text-white hover:bg-[#0037CC]">
            {busy ? "Creating…" : "Create lead"}
          </button>
        </div>
      </form>
    </div>
  );
}
